/**
 * SuperAdmin Auth Store
 * Zustand store for SuperAdmin authentication state
 */

import { create } from 'zustand';
import { authApi } from '../utils/api';

/**
 * ¿El token guardado sigue vigente? Se lee el `exp` del JWT sin llamar al
 * servidor. Un token vencido en localStorage hacía que el login "entrara"
 * (había token) y después toda opción fallara con 401.
 */
export function tokenVigente(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !payload.exp || payload.exp * 1000 > Date.now() + 5000;
  } catch {
    return false;
  }
}

const tokenInicial = localStorage.getItem('superadmin_token');
if (tokenInicial && !tokenVigente(tokenInicial)) {
  localStorage.removeItem('superadmin_token');
  localStorage.removeItem('superadmin_user');
}

const useSuperadminAuthStore = create((set, get) => ({
  user: JSON.parse(localStorage.getItem('superadmin_user') || 'null'),
  token: localStorage.getItem('superadmin_token') || null,
  isAuthenticated: tokenVigente(localStorage.getItem('superadmin_token')),
  isLoading: false,
  error: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authApi.login({ email, password });
      const { token, user } = response.data;

      localStorage.setItem('superadmin_token', token);
      localStorage.setItem('superadmin_user', JSON.stringify(user));

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error de autenticación';
      set({ isLoading: false, error: errorMsg });
      return { success: false, error: errorMsg };
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout API errors
    }

    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('superadmin_user');

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null
    });
  },

  verifyToken: async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!tokenVigente(token)) {
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      set({ isAuthenticated: false, user: null, token: null });
      return false;
    }

    try {
      const response = await authApi.verify();
      const { user } = response.data;

      localStorage.setItem('superadmin_user', JSON.stringify(user));
      set({ user, isAuthenticated: true });
      return true;
    } catch (error) {
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      set({ isAuthenticated: false, user: null, token: null });
      return false;
    }
  },

  refreshUser: async () => {
    try {
      const response = await authApi.me();
      const { user } = response.data;
      localStorage.setItem('superadmin_user', JSON.stringify(user));
      set({ user });
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    set({ isLoading: true, error: null });

    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });

      set({ isLoading: false });
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al cambiar contraseña';
      set({ isLoading: false, error: errorMsg });
      return { success: false, error: errorMsg };
    }
  },

  clearError: () => set({ error: null })
}));

export default useSuperadminAuthStore;
